<template>
  <div class="dashboard">

    <el-select v-model="selectedComponent" :placeholder="$t('placeholder.selectComponent')">
      <el-option
        v-for="item in options"
        :key="item.label"
        :label="$t(item.label)"
        :value="item.value">
      </el-option>
    </el-select>

    <el-button @click="addDashoardItem" :disabled="!selectedComponent">Add new</el-button>
    <el-button @click="frozen = !frozen">Lock</el-button>

    <grid-layout
      :layout='layout'
      :col-num='4'
      :row-height="1"
      :is-draggable='!frozen'
      :is-resizable='!frozen'
      :vertical-compact='false'
      :margin='[10, 10]'
      :use-css-transforms='true'>

      <grid-item v-for='item in layout'
        :x='item.x'
        :y='item.y'
        :w='item.w'
        :h='item.h'
        :i='item.i'>

        <div v-if="item.component" class="card card--small">

          <div v-if="item.component.title || item.component.configName" class="header">

            <div v-show="!frozen" class="config-icon" @click="removeComponent(item.i)">
              <fa-icon icon="trash-o" />
            </div>

            <div
              v-if="item.component.configName"
              v-show="!frozen"
              class="config-icon"
              @click="item.component.isConfiguring = !item.component.isConfiguring">

              <fa-icon :icon="item.component.isConfiguring ? 'floppy-o' : 'cog'" />
            </div>

            <h4 class="title" >
              <live-edit v-model="item.component.title" :editable="!frozen" />
            </h4>

          </div>

          <div v-if="item.component.isConfiguring" class="config-box">
            <component
              :is="item.component.configName"
              v-bind="item.component.properties"
              @propertiesChanged="updateComponent(item.component, $event)"/>
          </div>

          <div v-else :class="{'padded-body': item.component.needsPadding}" class="body">
            <component
              ref="item.component.ref"
              :is="item.component.name"
              v-bind="item.component.properties"/>
          </div>

        </div>

      </grid-item>

    </grid-layout>

  </div>
</template>

<script>
import { GridLayout, GridItem } from 'vue-grid-layout'
import LiveEdit from './LiveEdit.vue'

import SystemTime from 'components/dashboard/SystemTime'
import SystemLocation from 'components/dashboard/SystemLocation'
import EventTable from 'components/event/EventTable'
import EventTableConfig from 'components/event/EventTableConfig'
import LANStatus from 'components/dashboard/LANStatus'
import SwitchControll from 'components/dashboard/SwitchControll'
import EmergencyFunctions from 'components/dashboard/EmergencyFunctions'
import EventStatistics from 'components/event/EventStatistics'
import EventStatisticsConfig from 'components/event/EventStatisticsConfig'

import defaultDashboard from 'components/dashboard/defaultDashboard'

export default {
  name: 'dashboard',
  data () {
    const dashboardItems = [{
      label: 'label.systemTime',
      value: {
        title: this.$t('label.systemTime'),
        name: 'SystemTime',
        w: 1,
        h: 8,
        needsPadding: true
      }
    }, {
      label: 'label.serverLocation',
      value: {
        title: this.$t('label.serverLocation'),
        name: 'SystemLocation',
        w: 1,
        h: 8,
        needsPadding: true
      }
    }, {
      label: 'label.events',
      value: {
        title: this.$t('label.events'),
        name: 'EventTable',
        configName: 'EventTableConfig',
        w: 2,
        h: 45,
        isConfiguring: false,
        properties: {
          showSearch: false // Hide search bar
        }
      }
    }, {
      label: 'label.LAN-status',
      value: {
        title: this.$t('label.LAN-status'),
        name: 'LANStatus',
        w: 1,
        h: 20,
        needsPadding: true
      }
    }, {
      label: 'label.switch-controll',
      value: {
        title: this.$t('label.switch-controll'),
        name: 'SwitchControll',
        w: 1,
        h: 20,
        needsPadding: true
      }
    }, {
      label: 'label.emergency-functions',
      value: {
        title: this.$t('label.emergency-functions'),
        name: 'EmergencyFunctions',
        w: 1,
        h: 9,
        needsPadding: true
      }
    }, {
      label: 'label.event-statistics',
      value: {
        title: this.$t('label.event-statistics'),
        name: 'EventStatistics',
        w: 2,
        h: 40,
        needsPadding: true,
        configName: 'EventStatisticsConfig',
        isConfiguring: false
      }
    }]
    return {
      layout: window.localStorage.layout ? JSON.parse(window.localStorage.layout) : defaultDashboard,
      frozen: false,
      selectedComponent: null,
      dashboardItems,
      options: dashboardItems.map(({ label, value }) => ({ label, value: value.name }))
    }
  },
  methods: {
    addDashoardItem () {
      const maxY = this.layout.map(i => i.y).reduce((a, i) => (i > a) ? i : a, 0)
      const component = Object.assign({}, this.dashboardItems.find(i => i.value.name === this.selectedComponent).value)

      this.layout.push({
        'x': 0,
        'y': maxY + 1,
        'w': component.w || 2,
        'h': component.h || 20,
        'i': this.layout.length + '',
        component
      })

      this.selectedComponent = null
    },
    updateComponent (component, properties) {
      component.properties = Object.assign({}, component.properties, properties)
    },
    removeComponent (i) {
      this.layout = this.layout.filter(item => item.i !== i)
    }
  },
  watch: {
    layout: {
      handler (layout) {
        // TODO: Save layout to database
        window.localStorage.layout = JSON.stringify(layout)
      },
      deep: true
    }
  },
  components: {
    GridLayout,
    GridItem,
    LiveEdit,
    SystemTime,
    SystemLocation,
    EventTable,
    EventTableConfig,
    LANStatus,
    SwitchControll,
    EmergencyFunctions,
    EventStatistics,
    EventStatisticsConfig
  }
}
</script>

<style scoped>
.card {
  height: 100%;
  min-height: 100%;
  will-change: transform;
  position: relative;
  border-radius: 2px;
  overflow: auto;
  background-color: #fafafa;
  -webkit-transition: all 400ms cubic-bezier(0.165, 0.84, 0.44, 1);
  transition: all 400ms cubic-bezier(0.165, 0.84, 0.44, 1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.37);
  padding-top: 40px;
}

.header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 10px;
}

.title {
  margin: 0;
}

.config-icon {
  float: right;
  cursor: pointer;
  margin-left: 10px;
}
.config-box {
  padding: 10px;
}

.body {
  overflow: auto;
  height: 100%;
}

.padded-body {
  padding: 0 10px 10px 10px;
}

.data-table {
  margin-bottom: 0 !important;
}
</style>


// WEBPACK FOOTER //
// Dashboard.vue?7dea77b8